import IconLamp from "@src/assets/icons/lamp.svg?react";
import IconX from "@src/assets/icons/tabler/x.svg?react";
import { openPlanModal } from "@src/components/plan/plan-modal/plan-modal";
import {
	PlanFamily,
	PlanStatus,
	SupportedFeature,
	SupportedPlan,
} from "@src/gql/graphql";
import clsx from "clsx";
import { motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { UPGRADE_CARD_DISMISSED_KEY } from "./constants";
import { useAvailableFeatures } from "./feature-gate/use-available-features";
import { useMyPlanSuspense } from "./queries/fetch-plan";

export function UpgradePopup() {
	const [showPopup, setShowPopup] = useState(false);
	const [dismissed, setDismissed] = useState(
		() => sessionStorage.getItem(UPGRADE_CARD_DISMISSED_KEY) === "true",
	);
	const { data: plan } = useMyPlanSuspense();
	const { availableFeatures } = useAvailableFeatures();

	useEffect(() => {
		const timer = setTimeout(() => {
			setShowPopup(true);
		}, 1000);

		return () => clearTimeout(timer);
	}, []);

	const openUpgradeModal = useCallback(() => {
		openPlanModal({ source: "upgrade_popup" });
	}, []);

	const handleDismiss = useCallback(() => {
		sessionStorage.setItem(UPGRADE_CARD_DISMISSED_KEY, "true");
		setDismissed(true);
	}, []);

	const isPaidActive =
		(plan.planFamily === PlanFamily.Pro ||
			plan.planFamily === PlanFamily.Team) &&
		plan.status === PlanStatus.Active;
	const isBeliever = plan.plan === SupportedPlan.Believer;

	if (isPaidActive || isBeliever || dismissed) {
		return null;
	}

	const isInTrial = plan.status === PlanStatus.InTrial;
	const isStarter = plan.planFamily === PlanFamily.Starter;
	const isNotActive =
		plan.status !== PlanStatus.Active && plan.status !== PlanStatus.Cancelled;
	const shouldShow = isStarter || isInTrial || isNotActive;

	const creditsStats = availableFeatures.find(
		(feature) => feature.feature === SupportedFeature.CreditsBudget,
	)?.stats;
	const hasCreditsQuota =
		creditsStats?.quota !== undefined && creditsStats.quota > 0;
	const percentUsed = hasCreditsQuota
		? Math.min(100, (creditsStats.current / creditsStats.quota) * 100)
		: 0;

	return (
		<>
			{showPopup && shouldShow && (
				<motion.div
					initial={{ y: 10, opacity: 0 }}
					animate={{ y: 0, opacity: 1 }}
					exit={{ y: 10, opacity: 0 }}
					transition={{ duration: 0.2, type: "spring", bounce: 0 }}
					className="w-full px-3"
				>
					<div
						className={clsx(
							"h-7.5 px-2.5 rounded-lg bg-surface-subtle/80",
							"border border-border-subtle",
							"flex items-center justify-between gap-1.5",
						)}
					>
						<p
							className={clsx(
								"truncate text-[11px] font-medium",
								"leading-tight text-text-secondary",
							)}
						>
							{hasCreditsQuota
								? `${Math.round(percentUsed)}% credits used`
								: "Unlock premium features"}
						</p>

						<div className="flex items-center gap-1 shrink-0">
							<button
								type="button"
								onClick={openUpgradeModal}
								className={clsx(
									"rounded-md px-1.5 py-0.5 text-[11px] font-semibold leading-none",
									"inline-flex items-center gap-1 cursor-pointer transition-colors",
									"text-brand-primary hover:bg-brand-primary/10",
								)}
							>
								<IconLamp className="w-3 h-3" />
								{isInTrial ? "Choose Plan" : "Upgrade"}
							</button>
							<button
								type="button"
								onClick={handleDismiss}
								aria-label="Dismiss"
								className={clsx(
									"flex size-4 shrink-0 items-center justify-center cursor-pointer",
									"text-text-muted hover:text-text-primary",
								)}
							>
								<IconX className="size-3" />
							</button>
						</div>
					</div>
				</motion.div>
			)}
		</>
	);
}
